// src/lib/test-engine/test-run-config.ts
//
// Per-attempt-kind run configuration. The full SAT and the Checkpoint
// diagnostic share the same engine, routes, flow pages, and Runner; this is the
// single place that varies between them (module counts, durations, pretest
// count, whether there's a break, and which assembly config to use).
//
// IMPORTANT: the SAT entry holds EXACTLY the constants the routes hardcoded
// before this change, so the full-SAT path is byte-identical.

import { PRETEST_PER_MODULE } from '@/lib/test-engine/pretest'

export type AttemptKind = 'SAT' | 'CHECKPOINT' | 'BENCHMARK'

export type TestRunConfig = {
  kind: AttemptKind
  /** Assembly config code (getAssemblyConfig) — 'sat' or 'checkpoint'. */
  testCode: string
  /** Questions per module. */
  counts: { RW: number; MATH: number }
  /** Module duration in seconds (before the extended-time multiplier). */
  seconds: { RW: number; MATH: number }
  /** Unscored pretest items per module. */
  pretest: { RW: number; MATH: number }
  /** Whether there's a 10-minute break between R&W and Math. */
  hasBreak: boolean
}

export const TEST_RUN_CONFIGS: Record<AttemptKind, TestRunConfig> = {
  SAT: {
    kind: 'SAT',
    testCode: 'sat',
    counts: { RW: 27, MATH: 22 },
    seconds: { RW: 32 * 60, MATH: 35 * 60 },
    pretest: { RW: PRETEST_PER_MODULE.RW, MATH: PRETEST_PER_MODULE.MATH },
    hasBreak: true,
  },
  /**
   * A full SAT in every timing and counting respect — the SAT entry's numbers
   * verbatim — differing ONLY in where its questions come from: a frozen form
   * rather than the canonical bank.
   *
   * That is the whole point. A benchmark has to be the same sitting as a real
   * full-length or its score is not on the same scale; the constancy is in the
   * CONTENT, not the shape. PRETEST ITEMS ARE ZERO: unscored trial questions
   * vary the denominator between students, which is precisely the variability a
   * benchmark exists to remove.
   */
  BENCHMARK: {
    kind: 'BENCHMARK',
    testCode: 'sat',
    counts: { RW: 27, MATH: 22 },
    seconds: { RW: 32 * 60, MATH: 35 * 60 },
    pretest: { RW: 0, MATH: 0 },
    hasBreak: true,
  },
  CHECKPOINT: {
    kind: 'CHECKPOINT',
    testCode: 'checkpoint',
    counts: { RW: 13, MATH: 10 },
    seconds: { RW: 15 * 60, MATH: 15 * 60 },
    pretest: { RW: 0, MATH: 0 }, // 2 unscored of ~11 would gut the denominator
    hasBreak: false,
  },
}

/**
 * Resolve the run config for an attempt kind, defaulting to the full SAT.
 * Case-insensitive so an API body ("checkpoint", "benchmark") and the Prisma
 * enum value ("CHECKPOINT", "BENCHMARK") both resolve.
 *
 * BENCHMARK must resolve here, not fall through to SAT. When it did, every
 * Benchmark branch in /api/sat/start and finish-module was dead code: the admin
 * "Start the Benchmark" button created an ordinary kind=SAT attempt on the
 * canonical bank, with pretest items, and spent the free monthly full-length.
 */
export function getTestRunConfig(kind: AttemptKind | string | null | undefined): TestRunConfig {
  const k = String(kind).toUpperCase()
  if (k === 'CHECKPOINT' || k === 'BENCHMARK') return TEST_RUN_CONFIGS[k]
  return TEST_RUN_CONFIGS.SAT
}
